/**
 * Centralized network stats tracker — provides live counters for
 * CF function calls, Firestore reads, RTDB events, and cache hits/misses.
 *
 * Gated by localStorage `devModeViewNetworkInfo`.
 * Components subscribe via `networkStats.subscribe(cb)` for live updates.
 */

export interface NetworkStatsSnapshot {
  cfCalls: number;
  firestoreReadCalls: number;
  firestoreReads: number;
  firestoreWrites: number;
  rtdbEvents: number;
  cacheHits: number;
  cacheMisses: number;
  idbCacheHits: number;
}

type Listener = (stats: NetworkStatsSnapshot) => void;
type SimpleListener = () => void;

const counters: NetworkStatsSnapshot = {
  cfCalls: 0,
  firestoreReadCalls: 0,
  firestoreReads: 0,
  firestoreWrites: 0,
  rtdbEvents: 0,
  cacheHits: 0,
  cacheMisses: 0,
  idbCacheHits: 0,
};

/** Cached immutable snapshot — only replaced when counters change.
 *  This is critical for useSyncExternalStore which requires referential
 *  stability from getSnapshot() between renders. */
let _cachedSnapshot: NetworkStatsSnapshot = { ...counters };

const listeners = new Set<Listener>();
const simpleListeners = new Set<SimpleListener>();

function notify() {
  // Create a new frozen snapshot object so useSyncExternalStore detects the change
  _cachedSnapshot = { ...counters };
  for (const cb of listeners) {
    try { cb(_cachedSnapshot); } catch { /* ignore */ }
  }
  for (const cb of simpleListeners) {
    try { cb(); } catch { /* ignore */ }
  }
}

export const networkStats = {
  /** Increment Cloud Function call count */
  cfCall() {
    counters.cfCalls++;
    notify();
  },

  /** Increment Firestore read call initiated count (one per getDoc/getDocs call) */
  firestoreReadCall() {
    counters.firestoreReadCalls++;
    notify();
  },

  /** Increment Firestore read count (documents returned) */
  firestoreRead(count = 1) {
    counters.firestoreReads += count;
    notify();
  },

  /** Increment Firestore write count (setDoc, updateDoc, deleteDoc) */
  firestoreWrite(count = 1) {
    counters.firestoreWrites += count;
    notify();
  },

  /** Increment RTDB event count */
  rtdbEvent() {
    counters.rtdbEvents++;
    notify();
  },

  /** Increment cache hit count */
  cacheHit() {
    counters.cacheHits++;
    notify();
  },

  /** Increment cache miss count */
  cacheMiss() {
    counters.cacheMisses++;
    notify();
  },

  /** Increment IndexedDB cache hit count */
  idbCacheHit() {
    counters.idbCacheHits++;
    notify();
  },

  /** Get current snapshot (referentially stable between changes) */
  getSnapshot(): NetworkStatsSnapshot {
    return _cachedSnapshot;
  },

  /** Reset all counters */
  reset() {
    counters.cfCalls = 0;
    counters.firestoreReadCalls = 0;
    counters.firestoreReads = 0;
    counters.firestoreWrites = 0;
    counters.rtdbEvents = 0;
    counters.cacheHits = 0;
    counters.cacheMisses = 0;
    counters.idbCacheHits = 0;
    notify();
  },

  /** Subscribe to counter changes (with snapshot arg) — returns unsubscribe function */
  subscribe(cb: Listener): () => void {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  },

  /** Subscribe for useSyncExternalStore (no args) — returns unsubscribe function */
  subscribeSimple(cb: SimpleListener): () => void {
    simpleListeners.add(cb);
    return () => { simpleListeners.delete(cb); };
  },

  /** Check if dev mode network info is enabled */
  isEnabled(): boolean {
    try {
      return localStorage.getItem('devModeViewNetworkInfo') === 'true';
    } catch {
      return false;
    }
  },
};
